import { type Observable as ObservableType, Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/from';
import 'rxjs/add/observable/of';
import 'rxjs/add/observable/empty';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/mergeMap';
import { log } from '@atlassian/jira-common-util-logging';
import { fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { UNASSIGNED_EPIC_ID } from '../../../common/constants';
import { hasJiraErrorMessages, parseJiraErrorMessages } from '../../../common/error';
import { getCardListId } from '../../../common/utils';
import {
	BACKLOG,
	KANBAN_BACKLOG_STRATEGY,
	REFRESH_SOURCE_FAILURE,
	REFRESH_SOURCE_KANBAN_CARD_MOVE_SUCCESS,
	SPRINT,
} from '../../../model/constants';
import { MoveCardsSelectTransitionType } from '../../../model/modals/types';
import type { Transition } from '../../../model/transition/types';
import type { CardList, CardListType } from '../../../model/types';
import type { TransitionAndRankResponse } from '../../../rest/cards-transition-and-rank/types';
import {
	getFirstTransitionScreen,
	getNewCardOrder,
	getPossibleTransitions,
	hasMultipleTransitions,
	hasTransitionScreens,
	isFailedTransitionAndRankResponse,
} from '../../../rest/cards-transition-and-rank/utils';
import cardsAssignToListService from '../../../services/cards-assign-to-list';
import cardsMoveAfterService from '../../../services/cards-move-after';
import cardsMoveBeforeService from '../../../services/cards-move-before';
import cardsMoveSprintService from '../../../services/cards-move-sprint';
import { transitionAndRankService } from '../../../services/cards-transition-and-rank';
import { makeServiceContext } from '../../../services/service-context';
import {
	transitionScreenDialogService,
	SOFTWARE_BACKLOG_ISSUE_TRANSITION,
} from '../../../services/transition-screen-dialog';
import type { TransitionScreenEvent } from '../../../services/transition-screen-dialog/types';
import { isShowEvent, isSuccessfulEvent } from '../../../services/transition-screen-dialog/utils';
import { type RefreshDataRequestAction, refreshDataRequest } from '../../../state/actions/app';
import {
	type SetCardOrderGlobalAction,
	setCardOrderGlobal,
} from '../../../state/actions/card-order-global';
import {
	type CardsMoveNonOptimisticRequestAction,
	type CardsTransitionAndRankFailureAction,
	type CardsMoveCancelAction,
	type CardsMoveSuccessAction,
	type CardsMoveDisabledAction,
	type CardsMoveFailureAction,
	type CardsMoveRequestAction,
	cardsMoveFailure,
	cardsMoveSuccess,
	cardsMoveDisabled,
	cardsMoveCancel,
	cardsTransitionAndRankFailure,
	CARDS_MOVE_REQUEST,
	CARDS_MOVE_NON_OPTIMISTIC_REQUEST,
} from '../../../state/actions/cards/move';
import type { OptimisticId } from '../../../state/actions/meta/optimistic-ui-meta';
import { type OpenModalAction, openModal } from '../../../state/actions/modals';
import {
	getBaseUrl,
	getRankCustomFieldId,
	getRapidViewId,
	getBacklogStrategy,
	isKanbanBacklogStrategy,
	getKanbanTargetColumnId,
	isCardMoveDisabled as getIsCardMoveDisabled,
} from '../../../state/selectors/app';
import {
	getCards,
	getCardLists,
	makeCardIdFromCardKey,
	makeCardKeyFromCardId,
} from '../../../state/selectors/cards';
import { canEditBoardConfig } from '../../../state/selectors/permissions';
import type { MiddlewareAPI, ActionsObservable, State, Dispatch } from '../../../state/types';

type MoveParams = {
	cardIds: number[];
	cardKeys: string[];
	cardList: CardList;
	cardListId: string;
	cardListType: CardListType;
	beforeCardKey: string | null;
	afterCardKey: string | null;
	baseUrl: string;
	rankCustomFieldId: number;
	rapidViewId: string;
	selectedTransition?: Transition;
};

const isCardsMoveNonOptimisticRequestAction = (
	action: CardsMoveRequestAction | CardsMoveNonOptimisticRequestAction,
): action is CardsMoveNonOptimisticRequestAction =>
	action && 'meta' in action && 'optimisticId' in action.meta;

const getOptimisticId = (
	action: CardsMoveRequestAction | CardsMoveNonOptimisticRequestAction,
): OptimisticId =>
	isCardsMoveNonOptimisticRequestAction(action)
		? action.meta.optimisticId
		: action.meta.optimistic.id;

const getTargetStatusId = (
	action: CardsMoveRequestAction | CardsMoveNonOptimisticRequestAction,
): string | undefined => {
	if (action.payload.selectedTransition) {
		return String(action.payload.selectedTransition.toStatusId);
	}
	return action.payload.targetStatusId;
};

const moveToSprint = (params: MoveParams) =>
	cardsMoveSprintService(
		params.baseUrl,
		params.rankCustomFieldId,
		Number(params.rapidViewId),
		Number(params.cardList.id),
		params.cardKeys,
		params.beforeCardKey,
		params.afterCardKey,
		false,
	);

const moveCardToList = (
	params: MoveParams,
	targetColumn: number | null,
	isCompanyManaged: boolean,
	isCmpKanban: boolean,
) =>
	isCompanyManaged
		? transitionAndRankService({
				rankCustomFieldId: params.rankCustomFieldId,
				issueKeys: params.cardKeys,
				targetColumn,
				rankBeforeIssueIdOrKey: params.beforeCardKey,
				rankAfterIssueIdOrKey: params.afterCardKey,
				rapidViewId: Number(params.rapidViewId),
				selectedTransitionId: params.selectedTransition?.id,
				calculateNewIssuesOrder: isCmpKanban,
			})
		: cardsAssignToListService(
				params.baseUrl,
				params.rankCustomFieldId,
				params.cardKeys,
				params.beforeCardKey,
				params.afterCardKey,
				Number(params.rapidViewId),
				params.cardListId,
			);

const moveCardToBacklog = (params: MoveParams) =>
	cardsMoveSprintService(
		params.baseUrl,
		params.rankCustomFieldId,
		Number(params.rapidViewId),
		null,
		params.cardKeys,
		params.beforeCardKey,
		params.afterCardKey,
		true,
	);

const moveInsideList = (
	params: MoveParams,
	errorLogAttributes?: { [key: string]: string | number | boolean | null },
) => {
	if (!params.beforeCardKey && !params.afterCardKey) {
		if (errorLogAttributes) {
			log.safeErrorWithoutCustomerData(
				'jsw.backlog.cards-move.request-epic',
				'Failed to rank cards due to missing beforeCardKey and afterCardKey',
				errorLogAttributes,
			);
		}
		return Observable.empty<never>();
	}
	if (params.afterCardKey) {
		return cardsMoveAfterService(
			params.baseUrl,
			params.rankCustomFieldId,
			params.cardKeys,
			params.afterCardKey,
		);
	}

	return cardsMoveBeforeService(
		params.baseUrl,
		params.rankCustomFieldId,
		params.cardKeys,
		params.beforeCardKey,
	);
};

export const moveCards = (
	action: CardsMoveRequestAction | CardsMoveNonOptimisticRequestAction,
	state: State,
	servicePayload: MoveParams,
): ObservableType<TransitionAndRankResponse | string | null> => {
	const {
		payload: { isBetweenLists, beforeCardId, afterCardId },
	} = action;

	const serviceContext = makeServiceContext(state);

	const { cardListType } = servicePayload;

	const errorLogAttributes = {
		beforeCardId,
		afterCardId,
		isCompanyManaged: serviceContext.isCompanyManaged,
	};

	// move cards within a Sprint or to a different Sprint list when in Scrum board
	if (cardListType === SPRINT) {
		if (serviceContext.isCompanyManaged) {
			return moveToSprint(servicePayload);
		}
		return moveToSprint(servicePayload);
	}

	// move cards within the backlog or to the backlog when in Scrum board
	if (getBacklogStrategy(state) === SPRINT && cardListType === BACKLOG) {
		if (serviceContext.isCompanyManaged) {
			return moveCardToBacklog(servicePayload);
		}
		return moveCardToBacklog(servicePayload);
	}

	const targetColumnId = getKanbanTargetColumnId(state)(servicePayload.cardListType);
	if (serviceContext.isCompanyManaged && isKanbanBacklogStrategy(state)) {
		// move cards from one list to another list for Kanban board
		if (isBetweenLists) {
			const isCompanyManagedKanban = true;
			return moveCardToList(
				servicePayload,
				targetColumnId,
				serviceContext.isCompanyManaged,
				isCompanyManagedKanban,
			);
		}

		// move cards within a list for Kanban board
		return moveInsideList(servicePayload, errorLogAttributes);
	}

	// move cards from one list to another list for Kanban board
	if (isBetweenLists) {
		return moveCardToList(
			servicePayload,
			targetColumnId,
			serviceContext.isCompanyManaged,
			isKanbanBacklogStrategy(state),
		);
	}

	// move cards within a list for Kanban board
	return moveInsideList(servicePayload, errorLogAttributes);
};

const rankForTransitionScreen =
	(action: CardsMoveRequestAction | CardsMoveNonOptimisticRequestAction, params: MoveParams) =>
	(
		transitionScreenEvent: TransitionScreenEvent,
	): ObservableType<CardsMoveSuccessAction | CardsMoveCancelAction> => {
		const {
			payload: { cardIds, cardListId, selectedTransition },
			meta: { analyticsEvent },
		} = action;

		const optimisticId = getOptimisticId(action);

		const analyticsEventProperties = {
			withSelectedTransition: !!selectedTransition,
		};

		if (isShowEvent(transitionScreenEvent)) {
			fireTrackAnalytics(analyticsEvent, 'transitionScreen show', analyticsEventProperties);
			return Observable.empty<never>();
		}

		if (isSuccessfulEvent(transitionScreenEvent)) {
			fireTrackAnalytics(analyticsEvent, 'transitionScreen success', analyticsEventProperties);
			return moveInsideList(params).map(() =>
				cardsMoveSuccess(
					optimisticId,
					cardIds,
					cardListId,
					analyticsEvent,
					getTargetStatusId(action),
				),
			);
		}

		fireTrackAnalytics(analyticsEvent, 'transitionScreen cancel', analyticsEventProperties);
		return Observable.of(cardsMoveCancel(optimisticId, analyticsEvent));
	};

type HandCardsMoveSuccessOutput =
	| CardsMoveSuccessAction
	| CardsMoveCancelAction
	| OpenModalAction
	| RefreshDataRequestAction
	| SetCardOrderGlobalAction;

const handleMoveCardSuccess =
	(
		action: CardsMoveRequestAction | CardsMoveNonOptimisticRequestAction,
		state: State,
		dispatch: Dispatch,
		params: MoveParams,
	) =>
	(
		response: TransitionAndRankResponse | string | null,
	): ObservableType<HandCardsMoveSuccessOutput> => {
		const serviceContext = makeServiceContext(state);

		const {
			payload: { cardIds, cardListId, isBetweenLists, beforeCardId, afterCardId },
			meta: { analyticsEvent },
		} = action;

		const optimisticId = getOptimisticId(action);

		const { cardKeys } = params;

		if (!serviceContext.isCompanyManaged) {
			return Observable.of(cardsMoveSuccess(optimisticId, cardIds, cardListId, analyticsEvent));
		}

		if (isFailedTransitionAndRankResponse(response) && hasMultipleTransitions(response)) {
			const transitions = getPossibleTransitions(response).map(
				(possibleTransition) =>
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					({
						id: possibleTransition.id,
						name: possibleTransition.name,
						hasScreen: possibleTransition.hasTransitionView,
						toStatusId: possibleTransition.targetStatus,
					}) as Transition,
			);

			return Observable.of(
				openModal({
					type: MoveCardsSelectTransitionType,
					payload: {
						cardKeys,
						transitions,
						moveCardsActionPayload: {
							cardIds,
							cardListId,
							isBetweenLists,
							beforeCardId,
							afterCardId,
						},
						optimisticId,
						analyticsEvent,
					},
				}),
			);
		}

		if (isFailedTransitionAndRankResponse(response) && hasTransitionScreens(response)) {
			const transition = getFirstTransitionScreen(response);
			if (!transition) {
				return Observable.of(cardsMoveCancel(optimisticId, analyticsEvent));
			}

			return transitionScreenDialogService({
				issueId: cardIds[0],
				issueKey: cardKeys[0],
				transitionId: transition.id,
				analyticsEvent,
				dispatch,
			}).flatMap(rankForTransitionScreen(action, params));
		}
		if (serviceContext.isCompanyManaged && getBacklogStrategy(state) === KANBAN_BACKLOG_STRATEGY) {
			const actions: HandCardsMoveSuccessOutput[] = [
				cardsMoveSuccess(
					optimisticId,
					cardIds,
					cardListId,
					analyticsEvent,
					getTargetStatusId(action),
				),
				// TODO: Review why we need this and avoid it on most cases
				// This will refresh
				refreshDataRequest(REFRESH_SOURCE_KANBAN_CARD_MOVE_SUCCESS),
			];

			const getCardIdFromCardKey = makeCardIdFromCardKey(state);
			const newCardOrder = getNewCardOrder(response)?.map((cardKey) =>
				getCardIdFromCardKey(cardKey),
			);

			if (newCardOrder) {
				actions.push(setCardOrderGlobal(newCardOrder));
			}

			return Observable.from(actions);
		}

		return Observable.of(
			cardsMoveSuccess(
				optimisticId,
				cardIds,
				cardListId,
				analyticsEvent,
				getTargetStatusId(action),
			),
		);
	};

const getCardMoveParams = (
	action: CardsMoveRequestAction | CardsMoveNonOptimisticRequestAction,
	state: State,
): MoveParams | null => {
	const {
		payload: { cardIds, cardListId, beforeCardId, afterCardId, selectedTransition },
	} = action;

	const rankCustomFieldId = getRankCustomFieldId(state);
	if (!rankCustomFieldId) {
		return null;
	}
	const baseUrl = getBaseUrl(state);

	const cardEntities = getCards(state);
	const cardLists = getCardLists(state);
	const isCardMoveDisabled = getIsCardMoveDisabled(state);

	if (cardListId === String(UNASSIGNED_EPIC_ID)) {
		return null;
	}

	const cardKeys = cardIds.map((id) => cardEntities[String(id)].key);
	const rapidViewId = getRapidViewId(state);

	const derivedCardListId = getCardListId(cardListId);
	const cardList = cardLists[derivedCardListId];

	const cardListType = cardList.type;

	const getCardKeyByCardId = makeCardKeyFromCardId(state);

	const beforeCardKey =
		!isCardMoveDisabled && beforeCardId ? getCardKeyByCardId(beforeCardId) : null;

	const afterCardKey = !isCardMoveDisabled && afterCardId ? getCardKeyByCardId(afterCardId) : null;

	return {
		cardIds,
		cardKeys,
		cardListId: derivedCardListId,
		cardList,
		cardListType,
		beforeCardKey,
		afterCardKey,
		baseUrl,
		rankCustomFieldId,
		rapidViewId,
		selectedTransition,
	};
};

export const cardsMoveRequestEpic = (action$: ActionsObservable, store: MiddlewareAPI) =>
	action$
		.ofType(CARDS_MOVE_REQUEST, CARDS_MOVE_NON_OPTIMISTIC_REQUEST)
		.mergeMap(
			(
				action,
			): Observable<
				| CardsMoveDisabledAction
				| CardsMoveSuccessAction
				| CardsMoveFailureAction
				| CardsMoveCancelAction
				| CardsTransitionAndRankFailureAction
				| RefreshDataRequestAction
				| OpenModalAction
				| SetCardOrderGlobalAction
			> => {
				const state = store.getState();
				const dispatch = store.dispatch.bind(store);

				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				const cardsMoveAction = action as
					| CardsMoveRequestAction
					| CardsMoveNonOptimisticRequestAction;
				const {
					payload: { cardIds, cardListId, isBetweenLists },
					meta: { analyticsEvent },
				} = cardsMoveAction;

				const optimisticId = getOptimisticId(cardsMoveAction);

				// Card move is disabled only if in a CMP board jql filter is not order by RANK
				const cardMoveDisabled = getIsCardMoveDisabled(state);
				if (!isBetweenLists && cardMoveDisabled) {
					return Observable.from([
						cardsMoveDisabled(
							optimisticId,
							analyticsEvent,
							getRapidViewId(state),
							canEditBoardConfig(state),
						),
						refreshDataRequest(REFRESH_SOURCE_FAILURE),
					]);
				}

				const cardMoveParams = getCardMoveParams(cardsMoveAction, state);
				if (!cardMoveParams) {
					return Observable.empty<never>();
				}

				const { cardKeys } = cardMoveParams;

				const serviceContext = makeServiceContext(state);

				if (
					serviceContext.isCompanyManaged &&
					getBacklogStrategy(state) === KANBAN_BACKLOG_STRATEGY &&
					cardMoveParams.selectedTransition &&
					cardMoveParams.selectedTransition.hasScreen
				) {
					return transitionScreenDialogService({
						issueId: cardIds[0],
						issueKey: cardKeys[0],
						transitionId: cardMoveParams.selectedTransition.id,
						analyticsEvent,
						dispatch,
					}).flatMap(rankForTransitionScreen(action, cardMoveParams));
				}

				return moveCards(cardsMoveAction, state, cardMoveParams)
					.flatMap(handleMoveCardSuccess(cardsMoveAction, state, dispatch, cardMoveParams))
					.catch((error: Error) => {
						log.safeErrorWithoutCustomerData(
							'backlog.cards-move.failure',
							'Failed to move cards',
							error,
						);

						fireTrackAnalytics(analyticsEvent, 'issueTransition failed', {
							triggerPointKey: SOFTWARE_BACKLOG_ISSUE_TRANSITION,
							isModalOpen: false,
						});

						return Observable.from([
							hasJiraErrorMessages(error)
								? cardsTransitionAndRankFailure(
										optimisticId,
										parseJiraErrorMessages(error),
										analyticsEvent,
									)
								: cardsMoveFailure(optimisticId, cardIds, cardListId, analyticsEvent, error),
							refreshDataRequest(REFRESH_SOURCE_FAILURE),
						]);
					});
			},
		);
